import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Icon}.
 *
 * All sizing classes resolve to design tokens from @atlas/tokens (brand).
 * No hardcoded values.
 *
 * @remarks
 * - Supports four sizes: sm (16px), md (20px, default), lg (24px), xl (32px)
 * - aria-hidden="true" by default (decorative)
 * - Inherits currentColor for stroke/fill
 *
 * @example
 * ```tsx
 * const classes = iconVariants({ size: 'md' })
 * // Returns: "inline-flex shrink-0 h-5 w-5"
 * ```
 */
export const iconVariants = cva('inline-flex shrink-0', {
	variants: {
		size: {
			sm: 'h-4 w-4',
			md: 'h-5 w-5',
			lg: 'h-6 w-6',
			xl: 'h-8 w-8',
		},
	},
	defaultVariants: { size: 'md' },
})

/**
 * Props for the {@link Icon} component.
 *
 * @property size - Icon size: 'sm' (16) | 'md' (20, default) | 'lg' (24) | 'xl' (32)
 * @property className - Optional additional Tailwind classes
 * @property children - SVG icon element (typically from lucide-react)
 * @property aria-hidden - Whether icon is decorative (default: true)
 * @property aria-label - Accessible label if icon is meaningful (removes aria-hidden)
 *
 * @example
 * ```tsx
 * <Icon size="md">
 *   <CheckIcon />
 * </Icon>
 * ```
 */
export interface IconProps
	extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
		VariantProps<typeof iconVariants> {
	children: React.ReactNode
	'aria-label'?: string
}

/**
 * Icon wrapper component for consistent sizing and accessibility.
 *
 * @remarks
 * - **Styling:** All sizing uses design tokens from `@atlas/tokens` (brand).
 *   No hardcoded values.
 * - **Sizes:** Small (16px), medium (20px, default), large (24px), xl (32px).
 * - **Color:** Inherits currentColor from parent (use Tailwind text-* utilities).
 * - **Accessibility:** Decorative by default (`aria-hidden="true"`). Provide `aria-label`
 *   for meaningful icons.
 * - **Usage:** Wrap lucide-react icons or custom SVG icons for consistent sizing.
 *
 * @example Basic usage (decorative)
 * ```tsx
 * import { Icon } from '@atlas/ui-primitives'
 * import { Check } from 'lucide-react'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       <Icon size="md">
 *         <Check />
 *       </Icon>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With accessible label
 * ```tsx
 * <Icon size="lg" aria-label="Success">
 *   <Check />
 * </Icon>
 * ```
 *
 * @example With color
 * ```tsx
 * <span className="text-ds-success">
 *   <Icon size="md">
 *     <Check />
 *   </Icon>
 * </span>
 * ```
 *
 * @see {@link iconVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/icon/icon.stories.tsx for visual examples
 */
export function Icon({ size, className, children, 'aria-label': ariaLabel, ...props }: IconProps) {
	return (
		<span
			{...props}
			role="img"
			className={cn(iconVariants({ size }), className)}
			aria-hidden={ariaLabel ? undefined : true}
			aria-label={ariaLabel}
		>
			{children}
		</span>
	)
}
